import React from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "user";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  lastAuthEvent: AuthChangeEvent | null;
  userRole: AppRole | null;
  isAdmin: boolean;
  clearLastAuthEvent: () => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [roleLoading, setRoleLoading] = React.useState(true);
  const [lastAuthEvent, setLastAuthEvent] = React.useState<AuthChangeEvent | null>(null);
  const [userRole, setUserRole] = React.useState<AppRole | null>(null);

  const fetchUserRole = React.useCallback(async (userId: string) => {
    setRoleLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user role:", error);
        setUserRole(null);
        return;
      }

      setUserRole(data?.role as AppRole | null);
    } catch (err) {
      console.error("Error fetching user role:", err);
      setUserRole(null);
    } finally {
      setRoleLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setLastAuthEvent(event);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Mark role as loading synchronously to prevent false "access denied" flashes
        setRoleLoading(true);
        // Fetch role after auth state changes (defer to avoid deadlocks)
        setTimeout(() => fetchUserRole(session.user.id), 0);
      } else {
        setUserRole(null);
        setRoleLoading(false);
      }
      
      setLoading(false);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setRoleLoading(true);
        fetchUserRole(session.user.id);
      } else {
        setRoleLoading(false);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserRole]);

  const clearLastAuthEvent = () => setLastAuthEvent(null);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRole(null);
  };

  const isAdmin = userRole === "admin";
  // Combined loading: true while auth OR role is still loading
  const combinedLoading = loading || (user !== null && roleLoading);

  return (
    <AuthContext.Provider
      value={{ user, session, loading: combinedLoading, lastAuthEvent, userRole, isAdmin, clearLastAuthEvent, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
